import React, { memo, useState } from "react";
import { useForm } from "react-final-form";
import { useNotify, useMutation, Button, useRedirect } from "react-admin";
import SaveIcon from "@material-ui/icons/Save";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  makeStyles,
  Typography,
} from "@material-ui/core";

interface SaveButtonProps {
  invalid: boolean;
  isCalculationButtonClicked: boolean;
}

const SaveButton = ({
  invalid,
  isCalculationButtonClicked,
}: SaveButtonProps) => {
  const [open, setOpen] = useState<boolean>(false);

  const notify = useNotify();
  const redirect = useRedirect();
  const form = useForm(); // get current form state
  const classes = useStyles();

  const [checkExistingStudent, { loading }] = useMutation(); // Mutation to check if student exists
  const [saveChild, { loading: loadingSave }] = useMutation(); // Mutation to save the student

  // Check the existing student within school by unique code (NRIC), DOB, and School ID
  // If there are no existing student found, save new student directly.
  // Otherwise, show pop up confirmation to save the student with the same (NRIC), DOB, and School ID
  const handleCheckExistingStudent = () => {
    // Always get latest form values when click the save button
    const values = form.getState().values;

    checkExistingStudent(
      {
        type: "create",
        resource: "students/checkExistingStudents",
        payload: {
          data: {
            uniqueCode: values.uniqueCode,
            dateOfBirth: values.dateOfBirth,
            schoolId: values.schoolId,
          },
        },
      },
      {
        onSuccess: ({ res }) => {
          if (res.json.length === 0) {
            handleSaveChild(values);
            return;
          }
          setOpen(true);
        },
        onFailure: (error) => {
          notify(
            `Failed Check existing student, error: ${error.message}`,
            "warning",
          );
        },
      },
    );
  };

  // Save student including conditional handling of schedules
  const handleSaveChild = (values: any) => {
    // Check if each day's CCA drop-off uses fixed location
    const isFixedLocationMonday =
      values.isCcaMonday && values.dropOffAddressType === "fixed_location";
    const isFixedLocationTuesday =
      values.isCcaTuesday && values.dropOffAddressType === "fixed_location";
    const isFixedLocationWednesday =
      values.isCcaWednesday && values.dropOffAddressType === "fixed_location";
    const isFixedLocationThursday =
      values.isCcaThursday && values.dropOffAddressType === "fixed_location";
    const isFixedLocationFriday =
      values.isCcaFriday && values.dropOffAddressType === "fixed_location";

    // Need to check If the form uses one fixed location for all days or different fix location for each day
    let fixedLocationMondayId = values.fixedLocationMondayId;
    let fixedLocationTuesdayId = values.fixedLocationTuesdayId;
    let fixedLocationWednesdayId = values.fixedLocationWednesdayId;
    let fixedLocationThursdayId = values.fixedLocationThursdayId;
    let fixedLocationFridayId = values.fixedLocationFridayId;
    if (values.isUseOneFixedLocation === true) {
      fixedLocationMondayId = values.fixedLocationId;
      fixedLocationTuesdayId = values.fixedLocationId;
      fixedLocationWednesdayId = values.fixedLocationId;
      fixedLocationThursdayId = values.fixedLocationId;
      fixedLocationFridayId = values.fixedLocationId;
    }

    saveChild(
      {
        type: "create",
        resource: "students",
        payload: {
          data: {
            parentId: values.parentId,
            schoolId: values.schoolId,
            levelId: values.levelId,
            levelGroupId: values.levelGroupId ? values.levelGroupId : null,
            firstName: values.firstName,
            lastName: values.lastName,
            gender: values.gender,
            macAddress: values.macAddress,
            dateOfBirth: values.dateOfBirth,
            dropOffAuth: values.dropOffAuth,
            status: "active",
            uniqueCode: values.uniqueCode,
            medicalCondition: values.medicalCondition ?? null,
            externalId: values.externalId ?? null,
            isRegisterRegularSchedule:
              !values.isNotTakeSchedule &&
              (values.scheduleType === "regular" ||
                values.scheduleType === "both"),
            isRegisterCcaSchedule:
              !values.isNotTakeSchedule &&
              (values.scheduleType === "cca" || values.scheduleType === "both"),
            regularStudentSchedule:
              values.isNotTakeSchedule || values.scheduleType === "cca"
                ? null
                : {
                    academicYearId: values.academicYearId,
                    startedAt: values.startedAt,
                    isTaking: true,
                    detail: {
                      lat: values.locationData?.LATITUDE,
                      lng: values.locationData?.LONGITUDE,
                      street: values.street,
                      unitNumber: values.unitNumber,
                      blockNumber: values.blockNumber,
                      postalCode: values.postalCode,
                      building: values.building,
                      isMonday: values.isMonday,
                      isTuesday: values.isTuesday,
                      isWednesday: values.isWednesday,
                      isThursday: values.isThursday,
                      isFriday: values.isFriday,
                      isFromSchool:
                        values.toAndFrom === "OneWayMorning" ? false : true,
                      isToSchool:
                        values.toAndFrom === "OneWayAfternoon" ? false : true,
                    },
                    isApplyStudentSubsidy: values.isApplyStudentSubsidy,
                    studentSubsidy: values.isApplyStudentSubsidy
                      ? {
                          periodStartDate: values.periodStartDate,
                          periodEndDate: values.periodEndDate,
                          subsidyPercentage: values.subsidyPercentage,
                          remark: values.remark,
                        }
                      : null,
                  },
            ccaStudentSchedule:
              values.isNotTakeSchedule || values.scheduleType === "regular"
                ? null
                : {
                    ccaAcademicYearId: values.ccaAcademicYearId,
                    isTaking: true,
                    dropOffType: values.dropOffAddressType,
                    startedAt: values.ccaStartedAt,
                    detail:
                      values.dropOffAddressType === "fixed_location"
                        ? {
                            isMonday: values.isCcaMonday,
                            isTuesday: values.isCcaTuesday,
                            isWednesday: values.isCcaWednesday,
                            isThursday: values.isCcaThursday,
                            isFriday: values.isCcaFriday,

                            fixedLocationMondayId: isFixedLocationMonday
                              ? fixedLocationMondayId
                              : null,
                            fixedLocationTuesdayId: isFixedLocationTuesday
                              ? fixedLocationTuesdayId
                              : null,
                            fixedLocationWednesdayId: isFixedLocationWednesday
                              ? fixedLocationWednesdayId
                              : null,
                            fixedLocationThursdayId: isFixedLocationThursday
                              ? fixedLocationThursdayId
                              : null,
                            fixedLocationFridayId: isFixedLocationFriday
                              ? fixedLocationFridayId
                              : null,

                            waveMondayId:
                              isFixedLocationMonday &&
                              values.allowSelectWave &&
                              values.waveMondayId
                                ? values.waveMondayId
                                : null,
                            waveTuesdayId:
                              isFixedLocationTuesday &&
                              values.allowSelectWave &&
                              values.waveTuesdayId
                                ? values.waveTuesdayId
                                : null,
                            waveWednesdayId:
                              isFixedLocationWednesday &&
                              values.allowSelectWave &&
                              values.waveWednesdayId
                                ? values.waveWednesdayId
                                : null,
                            waveThursdayId:
                              isFixedLocationThursday &&
                              values.allowSelectWave &&
                              values.waveThursdayId
                                ? values.waveThursdayId
                                : null,
                            waveFridayId:
                              isFixedLocationFriday &&
                              values.allowSelectWave &&
                              values.waveFridayId
                                ? values.waveFridayId
                                : null,

                            isFromCca: true,
                          }
                        : {
                            isMonday: values.isCcaMonday,
                            isTuesday: values.isCcaTuesday,
                            isWednesday: values.isCcaWednesday,
                            isThursday: values.isCcaThursday,
                            isFriday: values.isCcaFriday,
                            isFromCca: true,
                            doorToDoorAddress: {
                              street: values.ccaStreet,
                              unitNumber: values.ccaUnitNumber,
                              blockNumber: values.ccaBlockNumber,
                              postalCode: values.ccaPostalCode,
                              building: values.ccaBuilding,
                              lat: values.ccaLocationData?.LATITUDE,
                              lng: values.ccaLocationData?.LONGITUDE,
                            },
                          },
                  },
          },
        },
      },
      {
        onSuccess: ({ res }) => {
          notify(`Successfully saved student`, "info");
          setOpen(false);
          redirect(`/parents/${res.json.parent.id}/show/2`);
        },
        onFailure: (error) => {
          notify(
            `Failed saved the student, error: ${error.message}`,
            "warning",
          );
        },
      },
    );
  };

  return (
    <>
      <Button
        label={loading || loadingSave ? `Loading...` : `Save`}
        disabled={
          loading ||
          loadingSave ||
          invalid ||
          (form.getState().values.isNotTakeSchedule
            ? false
            : !isCalculationButtonClicked)
        }
        onClick={handleCheckExistingStudent}
      >
        <SaveIcon />
      </Button>

      <Dialog
        open={open}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        className={classes.dialog}
        maxWidth="sm"
      >
        <DialogTitle id="alert-dialog-title">
          Overwrite Confirmation
        </DialogTitle>
        <DialogContent>
          <Typography>
            NRIC + DOB is not unique, would you like to overwrite it?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              // Always get latest form values when click the save button
              handleSaveChild(form.getState().values);
            }}
            color="primary"
            undoable={false}
            label={loading || loadingSave ? `Loading...` : `Yes`}
            disabled={loading || loadingSave}
          />
          <Button
            onClick={() => {
              setOpen(false);
            }}
            color="secondary"
            label="Cancel"
            className={classes.cancelButton}
            disabled={loading || loadingSave}
          />
        </DialogActions>
      </Dialog>
    </>
  );
};

const useStyles = makeStyles({
  dialog: {
    "& .MuiDialog-paper": {
      overflowY: "unset",
    },
  },
  cancelButton: {
    backgroundColor: "#eeeeee",
    color: "black",
  },
});

export default memo(SaveButton);
